import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import PmvComponentFormModal from '@/components/pmv/PmvComponentFormModal';
import pmvService from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';

const { Text, Title } = Typography;

/**
 * Digital Component Passport — /asset/pmv/components/:id
 */
const PmvComponentPassportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.component.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const [editOpen, setEditOpen] = useState(false);

  const q = useQuery({
    queryKey: ['pmv-component-passport', id],
    queryFn: () => pmvService.getComponentPassport(String(id)),
    enabled: Boolean(id),
    retry: false,
  });

  const data = q.data;
  const hero = data?.hero;
  const family = String(hero?.family || '');

  const tabItems = useMemo(() => {
    if (!data) return [];
    const aftercareKey = family === 'TYRE' ? 'aftercare' : 'repairs';
    const aftercareLabel = family === 'TYRE' ? 'Repair / Retread' : 'Repairs';
    return [
      { key: 'overview', label: 'Overview' },
      { key: 'installations', label: 'Installation History' },
      { key: 'condition', label: 'Condition & Inspection' },
      { key: aftercareKey, label: aftercareLabel },
      { key: 'warranty', label: 'Warranty' },
      { key: 'cost', label: 'Cost & Performance' },
      { key: 'documents', label: 'Documents' },
      { key: 'genealogy', label: 'Genealogy' },
      { key: 'integration', label: 'Integration' },
    ];
  }, [data, family]);

  if (q.isLoading) {
    return (
      <WorkspaceLayout>
        <div role="status" aria-live="polite" aria-busy="true">
          <Spin tip="Loading component passport…" />
        </div>
      </WorkspaceLayout>
    );
  }

  if (q.isError || !data || !hero) {
    return (
      <WorkspaceLayout>
        <Alert
          type="error"
          message="Component not found"
          action={
            <Button onClick={() => navigate('/asset/pmv/components')}>Back to register</Button>
          }
        />
      </WorkspaceLayout>
    );
  }

  const primary = data.primary_action;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Component"
        documentNumber={String(hero.component_number || '')}
        subtitle={[hero.brand, hero.model, hero.size_or_spec, hero.definition_name]
          .filter(Boolean)
          .join(' · ')}
        primaryActions={
          <Space wrap>
            {primary ? (
              <Button type="primary" onClick={() => navigate(primary.href)}>
                {primary.label}
              </Button>
            ) : null}
            {canManage ? (
              <Button onClick={() => setEditOpen(true)}>Edit</Button>
            ) : null}
            <Button onClick={() => navigate('/asset/pmv/components')}>Register</Button>
            {data.equipment?.pmv_profile_id ? (
              <Button onClick={() => navigate(`/asset/pmv/${data.equipment!.pmv_profile_id}`)}>
                Equipment
              </Button>
            ) : null}
          </Space>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]}>
          <Col xs={12} md={6}>
            <Text type="secondary">Status</Text>
            <div>
              <Tag color={hero.status === 'INSTALLED' ? 'blue' : undefined}>{hero.status}</Tag>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Equipment</Text>
            <div>
              {data.equipment?.pmv_profile_id ? (
                <Link to={`/asset/pmv/${data.equipment.pmv_profile_id}`}>
                  {hero.equipment_label || 'Unit'}
                </Link>
              ) : (
                hero.equipment_label || '—'
              )}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Position</Text>
            <div>{hero.position_code || '—'}</div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Installed</Text>
            <div>
              {hero.installed_at
                ? new Date(String(hero.installed_at)).toLocaleDateString()
                : '—'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Usage (interval)</Text>
            <div>
              {hero.usage?.status === 'OK' && hero.usage.usage != null
                ? String(hero.usage.usage)
                : hero.usage?.reason || 'Insufficient meter data'}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Text type="secondary">Condition</Text>
            <div>{hero.condition_code || '—'}</div>
          </Col>
          {family === 'TYRE' ? (
            <Col xs={12} md={6}>
              <Text type="secondary">Tread</Text>
              <div>{hero.tread_mm != null ? `${hero.tread_mm} mm` : '—'}</div>
            </Col>
          ) : null}
          <Col xs={12} md={6}>
            <Text type="secondary">Warranty</Text>
            <div>{hero.warranty}</div>
          </Col>
        </Row>
        {data.attention?.hints?.length ? (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Attention hints"
            description={data.attention.hints.join(' · ')}
          />
        ) : null}
      </Card>

      <Tabs
        activeKey={tab === 'aftercare' || tab === 'repairs' ? tab : tab}
        onChange={(k) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', k);
          setSearchParams(next);
        }}
        items={tabItems.map((t) => ({
          ...t,
          children: (
            <PassportTabBody
              tabKey={t.key}
              data={data}
              family={family}
              componentId={String(id)}
            />
          ),
        }))}
      />

      <PmvComponentFormModal
        open={editOpen}
        mode="edit"
        edit={{
          id: String(id),
          family: String(hero.family || ''),
          component_number: String(hero.component_number || ''),
          serial_number: data.component?.serial_number ?? null,
          condition_code: hero.condition_code,
          current_location_type: data.component?.current_location_type || 'WAREHOUSE',
          current_location_reference: data.component?.current_location_reference ?? null,
          is_active: true,
        }}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['pmv-component-passport', id] });
        }}
      />
    </WorkspaceLayout>
  );
};

function PassportTabBody({
  tabKey,
  data,
  family,
  componentId,
}: {
  tabKey: string;
  data: NonNullable<Awaited<ReturnType<typeof pmvService.getComponentPassport>>>;
  family: string;
  componentId: string;
}) {
  if (tabKey === 'overview') {
    const c = data.component;
    return (
      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
        <Descriptions.Item label="Family">{c.family}</Descriptions.Item>
        <Descriptions.Item label="Serial">{c.serial_number || '—'}</Descriptions.Item>
        <Descriptions.Item label="Definition">
          {c.definition?.code} — {c.definition?.name}
        </Descriptions.Item>
        <Descriptions.Item label="Location">
          {c.current_location_type}
          {c.current_location_reference ? ` · ${c.current_location_reference}` : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Purchase ref">{c.purchase_reference || '—'}</Descriptions.Item>
        <Descriptions.Item label="Provenance">{c.provenance}</Descriptions.Item>
      </Descriptions>
    );
  }

  if (tabKey === 'installations') {
    const rows = data.genealogy?.installations ?? [];
    if (!rows.length) return <Empty description="No installation history" />;
    return (
      <Timeline
        items={rows.map((inst: Record<string, unknown>) => ({
          children: (
            <div>
              <Text strong>{String(inst.position_code || '')}</Text>
              <Text type="secondary">
                {' '}
                · {inst.installed_at ? new Date(String(inst.installed_at)).toLocaleString() : ''}
                {inst.removed_at
                  ? ` → removed ${new Date(String(inst.removed_at)).toLocaleString()}`
                  : ' (active)'}
              </Text>
              <div>
                <Link to={`/asset/pmv/${String(inst.pmv_profile_id)}`}>Open equipment</Link>
              </div>
            </div>
          ),
        }))}
      />
    );
  }

  if (tabKey === 'condition') {
    if (family === 'TYRE') {
      const tyre = data.tyre?.tyre;
      const inspections = (data.tyre_inspections as unknown[]) || [];
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Size">{String(tyre?.size_code ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="Tread">
              {tyre?.latest_tread_depth_mm != null ? `${tyre.latest_tread_depth_mm} mm` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Pressure">
              {tyre?.latest_pressure_psi != null ? `${tyre.latest_pressure_psi} psi` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Exception">
              {String(tyre?.latest_pressure_exception ?? '—')}
            </Descriptions.Item>
          </Descriptions>
          <Title level={5}>Inspections</Title>
          {inspections.length ? (
            <Timeline
              items={inspections.slice(0, 10).map((row: unknown) => {
                const r = row as Record<string, unknown>;
                return {
                  children: `${r.inspected_at ? new Date(String(r.inspected_at)).toLocaleString() : ''} · ${r.overall_result || r.result || 'recorded'}`,
                };
              })}
            />
          ) : (
            <Empty description="No inspections yet" />
          )}
        </Space>
      );
    }
    if (family === 'BATTERY') {
      const bat = data.battery?.battery;
      const inspections = (data.battery_inspections as unknown[]) || [];
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Type">{String(bat?.battery_type ?? '—')}</Descriptions.Item>
            <Descriptions.Item label="Voltage">
              {bat?.latest_voltage != null ? `${bat.latest_voltage} V` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="SOH">
              {bat?.latest_soh_pct != null ? `${bat.latest_soh_pct}%` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Health source">
              {String(bat?.latest_health_source ?? '—')}
            </Descriptions.Item>
          </Descriptions>
          <Title level={5}>Health inspections</Title>
          {inspections.length ? (
            <Timeline
              items={inspections.slice(0, 10).map((row: unknown) => {
                const r = row as Record<string, unknown>;
                return {
                  children: `${r.inspected_at ? new Date(String(r.inspected_at)).toLocaleString() : ''} · SOH ${r.soh_pct ?? '—'} · ${r.health_source || ''}`,
                };
              })}
            />
          ) : (
            <Empty description="No battery inspections yet" />
          )}
        </Space>
      );
    }
    return <Empty description="No specialised condition panel for this family" />;
  }

  if (tabKey === 'aftercare' || tabKey === 'repairs') {
    const repairs = (data.repairs as unknown[]) || [];
    const retreads = (data.retreads as unknown[]) || [];
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={5}>Repairs</Title>
          {repairs.length ? (
            <Timeline
              items={repairs.map((row: unknown) => {
                const r = row as Record<string, unknown>;
                return {
                  children: `${r.repaired_at ? new Date(String(r.repaired_at)).toLocaleDateString() : ''} · ${r.repair_type || ''} · ${r.damage_summary || ''}`,
                };
              })}
            />
          ) : (
            <Empty description="No repairs recorded" />
          )}
        </div>
        {family === 'TYRE' ? (
          <div>
            <Title level={5}>Retreads</Title>
            {retreads.length ? (
              <Timeline
                items={retreads.map((row: unknown) => {
                  const r = row as Record<string, unknown>;
                  return {
                    children: `${r.status || ''} · count ${r.retread_sequence ?? r.sequence ?? ''} · same casing identity`,
                  };
                })}
              />
            ) : (
              <Empty description="No retreads" />
            )}
          </div>
        ) : null}
      </Space>
    );
  }

  if (tabKey === 'warranty') {
    const warranties = (data.warranties as unknown[]) || [];
    if (!warranties.length) {
      return (
        <Empty
          description={`Component warranty window: ${data.hero.warranty}${
            data.hero.warranty_end ? ` (ends ${String(data.hero.warranty_end).slice(0, 10)})` : ''
          }`}
        />
      );
    }
    return (
      <Timeline
        items={warranties.map((row: unknown) => {
          const r = row as Record<string, unknown>;
          return {
            children: `${r.kind || r.warranty_kind || ''} · ${r.claim_status || r.status || ''} · ${r.vendor_or_issuer || ''}`,
          };
        })}
      />
    );
  }

  if (tabKey === 'cost') {
    const perf = data.cost?.performance as
      | {
          net_lifecycle_cost?: {
            status: string;
            net: number | null;
            currency: string | null;
            reason: string | null;
            lines?: Array<{ kind: string; amount: number; source: string }>;
          };
          unit_costs?: Record<string, { status: string; value: number | null; reason: string | null; basis: string }>;
          replacement?: { status: string; advisory: string | null; note: string; sample_n: number };
          wear_remaining_pct?: number | null;
          peer_benchmark?: { note: string; n: number; median: number | null };
          warranty_intelligence?: Array<{ provider_name: string; claim_status: string; candidate_claim?: boolean }>;
          removal_reasons?: Array<{ reason: string; count: number }>;
        }
      | null
      | undefined;
    if (!perf) {
      return <Alert type="info" showIcon message="Cost performance unavailable" />;
    }
    const net = perf.net_lifecycle_cost;
    const units = perf.unit_costs || {};
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="Lifecycle cost ≠ GL expense"
          description={data.cost?.note || 'Amounts only from recorded purchase/repair/retread sources'}
        />
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Net lifecycle cost">
            {net?.status === 'OK' && net.net != null
              ? `${net.net}${net.currency ? ` ${net.currency}` : ''}`
              : net?.reason || 'Unavailable'}
          </Descriptions.Item>
          <Descriptions.Item label="Wear remaining">
            {perf.wear_remaining_pct != null ? `${perf.wear_remaining_pct}%` : 'Insufficient life data'}
          </Descriptions.Item>
          {Object.entries(units).map(([key, u]) => (
            <Descriptions.Item key={key} label={`Cost / ${u.basis}`}>
              {u.status === 'OK' && u.value != null ? String(u.value) : u.reason || 'Unavailable'}
            </Descriptions.Item>
          ))}
        </Descriptions>
        {perf.replacement ? (
          <Alert
            type={perf.replacement.status === 'ADVISORY' ? 'warning' : 'info'}
            showIcon
            message="Replacement advisory"
            description={
              perf.replacement.advisory ||
              `${perf.replacement.note} (N=${perf.replacement.sample_n})`
            }
          />
        ) : null}
        {perf.peer_benchmark ? (
          <Text type="secondary">{perf.peer_benchmark.note}</Text>
        ) : null}
        {(perf.removal_reasons?.length ?? 0) > 0 ? (
          <div>
            <Title level={5}>Removal reasons</Title>
            <Space wrap>
              {perf.removal_reasons!.map((r) => (
                <Tag key={r.reason}>
                  {r.reason} · {r.count}
                </Tag>
              ))}
            </Space>
          </div>
        ) : null}
        {(perf.warranty_intelligence?.length ?? 0) > 0 ? (
          <div>
            <Title level={5}>Warranty intelligence</Title>
            <Timeline
              items={perf.warranty_intelligence!.map((w) => ({
                children: `${w.provider_name} · ${w.claim_status}${
                  w.candidate_claim ? ' · claim candidate (human review)' : ''
                }`,
              }))}
            />
          </div>
        ) : null}
      </Space>
    );
  }

  if (tabKey === 'documents') {
    return (
      <EntityAttachmentsCard
        entityType="pmv_component"
        entityId={componentId}
        title="Component documents"
      />
    );
  }

  if (tabKey === 'genealogy') {
    const events = data.genealogy?.events ?? [];
    if (!events.length) return <Empty description="No lifecycle events" />;
    return (
      <Timeline
        items={events.map((ev: Record<string, unknown>) => ({
          children: (
            <div>
              <Tag>{String(ev.event_type || '')}</Tag>
              <Text type="secondary">
                {ev.occurred_at ? new Date(String(ev.occurred_at)).toLocaleString() : ''}
              </Text>
              <div>{String(ev.detail || '')}</div>
            </div>
          ),
        }))}
      />
    );
  }

  if (tabKey === 'integration') {
    const links = data.integration?.links ?? [];
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Links only"
          description={data.integration?.boundaries?.note}
        />
        {links.length ? (
          <Descriptions bordered size="small" column={1}>
            {links.map((l: Record<string, unknown>) => (
              <Descriptions.Item
                key={String(l.id)}
                label={`${l.link_type}${l.authority ? ` · ${l.authority}` : ''}`}
              >
                {String(l.external_number || l.external_id || '—')}
              </Descriptions.Item>
            ))}
          </Descriptions>
        ) : (
          <Empty description="No integration links" />
        )}
      </Space>
    );
  }

  return null;
}

export default PmvComponentPassportPage;
